<template>
  <div class="video-call-page" :class="{ 'notes-open': notesOpen }">
    <!-- Header -->
    <div class="video-call-header">
      <span class="meeting-code-label">
        <template v-if="tele">
          <i class="fe fe-video mr-1"></i>Telemedicine · {{ tele.data.patient_name }} ({{ tele.data.patient_id_card }})
          <span class="tele-reason" :title="tele.data.reason">{{ tele.data.reason }}</span>
        </template>
        <template v-else>Consultation: {{ channelName }}</template>
      </span>
      <div class="controls">
        <button
          v-if="tele && tele.is_consulting_doctor"
          class="ctrl-btn encounter-btn"
          @click="openEncounter"
          :title="tele.data.service_summary_id ? 'View encounter' : 'Record encounter / refer (opens a new tab, the call continues)'"
        >
          <i class="fe fe-file-plus"></i>
        </button>
        <button class="ctrl-btn" :class="{ muted: !micOn }" @click="toggleMic" :title="micOn ? 'Mute' : 'Unmute'">
          <i :class="micOn ? 'fe fe-mic' : 'fe fe-mic-off'"></i>
        </button>
        <button class="ctrl-btn" :class="{ muted: !camOn }" @click="toggleCam" :title="camOn ? 'Stop Video' : 'Start Video'">
          <i :class="camOn ? 'fe fe-video' : 'fe fe-video-off'"></i>
        </button>
        <button
          v-if="!patientToken"
          class="ctrl-btn"
          :class="{ recording: dictating }"
          @click="toggleDictation"
          :title="dictating ? 'Stop Dictation' : 'Start Dictation'"
        >
          <i :class="dictating ? 'fe fe-activity' : 'fe fe-edit-3'"></i>
        </button>
        <button v-if="!patientToken" class="ctrl-btn notes-btn" :class="{ active: notesOpen }" @click="notesOpen = !notesOpen" title="Toggle Notes">
          <i class="fe fe-file-text"></i>
        </button>
        <button class="ctrl-btn end-btn" @click="leaveCall" title="End Call">
          <i class="fe fe-phone-off"></i>
        </button>
      </div>
    </div>

    <!-- Main area: video + notes panel -->
    <div class="main-area">
      <!-- Video -->
      <div class="video-grid">
        <div class="video-wrapper remote-video-wrapper">
          <div id="remote-video" class="video-player">
            <div v-if="!remoteJoined" class="video-placeholder">
              <i class="fe fe-user" style="font-size:3rem"></i>
              <p>Waiting for other participant...</p>
            </div>
          </div>
          <span class="video-label" v-if="remoteJoined">Remote</span>
        </div>

        <div class="video-wrapper local-video-wrapper">
          <div id="local-video" class="video-player"></div>
          <span class="video-label">You</span>
        </div>
      </div>

      <!-- Notes Panel -->
      <div class="notes-panel" v-show="notesOpen">
        <div class="notes-header">
          <span>Consultation Notes</span>
          <div class="notes-header-actions">
            <span v-if="dictating" class="dictating-badge">
              <span class="pulse-dot"></span> Listening...
            </span>
            <button class="notes-icon-btn" @click="clearNotes" title="Clear notes">
              <i class="fe fe-trash-2"></i>
            </button>
          </div>
        </div>

        <div class="notes-body">
          <!-- Interim (live) transcript shown in grey -->
          <div v-if="interimText" class="interim-text">{{ interimText }}</div>

          <textarea
            ref="notesArea"
            class="notes-textarea"
            v-model="notes"
            placeholder="Notes will appear here as you speak, or type manually..."
          ></textarea>
        </div>

        <div class="notes-footer">
          <button
            class="btn-dictate"
            :class="{ active: dictating }"
            @click="toggleDictation"
          >
            <i :class="dictating ? 'fe fe-stop-circle' : 'fe fe-mic'"></i>
            {{ dictating ? 'Stop Dictation' : 'Start Dictation' }}
          </button>
          <button class="btn-save" @click="saveNotes" :disabled="saving || !notes.trim()">
            <i class="fe fe-save"></i>
            {{ saving ? 'Saving...' : 'Save Notes' }}
          </button>
        </div>

        <div v-if="saveSuccess" class="save-success">
          <i class="fe fe-check-circle"></i> Notes saved successfully
        </div>
        <div v-if="saveError" class="save-error">
          <i class="fe fe-alert-circle"></i> {{ saveError }}
        </div>
      </div>
    </div>

    <div v-if="errorMsg" class="alert alert-danger m-3" style="position:absolute;bottom:0;left:0;right:0;z-index:30">{{ errorMsg }}</div>
    <div v-if="connecting" class="connecting-overlay">
      <div class="spinner-border text-light" role="status"></div>
      <p class="text-white mt-2">Connecting...</p>
    </div>
  </div>
</template>

<script>
import AgoraRTC from "agora-rtc-sdk-ng";
import axios from "axios";

export default {
  name: "VideoCall",
  data() {
    return {
      channelName: this.$route.params.meetingCode || "",
      appointmentId: this.$route.query.apptId || this.$route.params.id || null,
      // Patient joining through their private link (/teleconsult/:id/call?t=…), not signed in.
      patientToken: this.$route.query.t || null,
      client: null,
      localTracks: { audio: null, video: null },
      micOn: true,
      camOn: true,
      remoteJoined: false,
      connecting: true,
      errorMsg: "",
      // Notes
      notesOpen: false,
      notes: "",
      interimText: "",
      dictating: false,
      recognition: null,
      saving: false,
      saveSuccess: false,
      saveError: "",
      // Telemedicine consultation detail, when this call belongs to one.
      tele: null,
    };
  },
  async mounted() {
    if (!this.patientToken) this.loadTelemedicine();
    await this.joinCall();
    this.initSpeechRecognition();
  },
  beforeDestroy() {
    this.stopDictation();
    this.cleanup();
  },
  methods: {
    loadTelemedicine() {
      if (!this.appointmentId) return;
      this.axios
        .get(`/api/v1/auth/telemedicine/consultations/${this.appointmentId}`)
        .then((res) => { this.tele = res.data; })
        .catch(() => { this.tele = null; }); // not a telemedicine consultation
    },
    // New tab, so the doctor keeps the call open while writing the encounter.
    openEncounter() {
      const route = this.tele.data.service_summary_id
        ? `/encounter/${this.tele.data.service_summary_id}`
        : `/add-record?appointment=${this.appointmentId}`;
      window.open(this.$router.resolve(route).href, "_blank", "noopener");
    },
    /**
     * Telemedicine consultations get their video token from our API, which checks that the user is the doctor
     * or the patient (or holds the patient's private link) and that it is the appointment time. Returns null
     * for other appointments (they keep the previous token service).
     */
    async telemedicineAccess() {
      if (!this.appointmentId) return null;
      try {
        const res = this.patientToken
          ? await this.axios.post(`/api/v1/teleconsult/${this.appointmentId}/join`, { t: this.patientToken })
          : await this.axios.post(`/api/v1/auth/telemedicine/consultations/${this.appointmentId}/join`);
        return res.data;
      } catch (err) {
        const status = err.response && err.response.status;
        if (status === 404 && !this.patientToken) return null; // not a telemedicine consultation
        throw new Error((err.response && err.response.data && err.response.data.message) || "You cannot join this consultation.");
      }
    },

    async joinCall() {
      let access = null;
      try {
        access = await this.telemedicineAccess();
      } catch (e) {
        this.errorMsg = e.message;
        this.connecting = false;
        return;
      }
      if (access) this.channelName = access.channel;
      const appId = access ? access.app_id : process.env.VUE_APP_AGORA_APP_ID;

      if (!appId) {
        this.errorMsg = "Agora App ID missing. Set VUE_APP_AGORA_APP_ID in your .env file and restart the dev server.";
        this.connecting = false;
        return;
      }

      try {
        this.client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });

        this.client.on("user-published", async (user, mediaType) => {
          await this.client.subscribe(user, mediaType);
          if (mediaType === "video") {
            this.remoteJoined = true;
            this.$nextTick(() => {
              user.videoTrack.play("remote-video");
            });
          }
          if (mediaType === "audio") {
            user.audioTrack.play();
          }
        });

        this.client.on("user-unpublished", (user, mediaType) => {
          if (mediaType === "video") {
            this.remoteJoined = false;
          }
        });

        this.client.on("user-left", () => {
          this.remoteJoined = false;
        });

        let uid;
        let token;
        if (access) {
          uid = access.uid;
          token = access.token;
        } else {
          uid = Math.floor(Math.random() * 99999) + 1;
          const tokenRes = await axios.post("https://api.insucare.ng/api/agora/token", {
            meeting_code: this.channelName,
            uid,
          });
          token = tokenRes.data.token;
        }

        await this.client.join(appId, this.channelName, token, uid);

        const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
        this.localTracks.audio = audioTrack;
        this.localTracks.video = videoTrack;

        videoTrack.play("local-video");
        await this.client.publish([audioTrack, videoTrack]);

        this.connecting = false;
      } catch (err) {
        console.error("Agora error:", err);
        this.errorMsg = "Could not connect to video call: " + (err.message || err);
        this.connecting = false;
      }
    },

    initSpeechRecognition() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        return; // browser doesn't support it — dictation buttons will still show but won't crash
      }

      const rec = new SpeechRecognition();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = "en-US";

      rec.onresult = (event) => {
        let interim = "";
        let final = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += t + " ";
          } else {
            interim += t;
          }
        }
        if (final) {
          this.notes += final;
          this.$nextTick(() => {
            if (this.$refs.notesArea) {
              this.$refs.notesArea.scrollTop = this.$refs.notesArea.scrollHeight;
            }
          });
        }
        this.interimText = interim;
      };

      rec.onerror = (e) => {
        if (e.error !== "no-speech") {
          console.warn("Speech recognition error:", e.error);
        }
      };

      // Auto-restart if it stops unexpectedly while dictating
      rec.onend = () => {
        if (this.dictating) {
          rec.start();
        }
      };

      this.recognition = rec;
    },

    toggleDictation() {
      if (!this.recognition) {
        this.errorMsg = "Speech recognition is not supported in this browser. Use Chrome or Edge.";
        return;
      }
      if (this.dictating) {
        this.stopDictation();
      } else {
        this.startDictation();
      }
    },

    startDictation() {
      this.notesOpen = true;
      this.dictating = true;
      this.interimText = "";
      this.recognition.start();
    },

    stopDictation() {
      this.dictating = false;
      this.interimText = "";
      if (this.recognition) {
        this.recognition.stop();
      }
    },

    clearNotes() {
      this.notes = "";
      this.interimText = "";
    },

    async saveNotes() {
      if (!this.notes.trim()) return;
      if (!this.appointmentId) {
        this.saveError = "Cannot save — appointment ID not found.";
        return;
      }
      this.saving = true;
      this.saveSuccess = false;
      this.saveError = "";
      try {
        await axios.put(`/api/v1/auth/appointments/${this.appointmentId}`, {
          meeting_notes: this.notes.trim(),
        });
        this.saveSuccess = true;
        setTimeout(() => { this.saveSuccess = false; }, 3000);
      } catch (err) {
        this.saveError = "Failed to save notes. Please copy them manually.";
      } finally {
        this.saving = false;
      }
    },

    async toggleMic() {
      if (this.localTracks.audio) {
        await this.localTracks.audio.setEnabled(!this.micOn);
        this.micOn = !this.micOn;
      }
    },
    async toggleCam() {
      if (this.localTracks.video) {
        await this.localTracks.video.setEnabled(!this.camOn);
        this.camOn = !this.camOn;
      }
    },
    async leaveCall() {
      this.stopDictation();
      this.cleanup();
      if (this.patientToken) {
        this.$router.push({ path: `/teleconsult/${this.appointmentId}`, query: { t: this.patientToken } });
        return;
      }
      this.$router.go(-1);
    },
    cleanup() {
      if (this.localTracks.audio) {
        this.localTracks.audio.close();
        this.localTracks.audio = null;
      }
      if (this.localTracks.video) {
        this.localTracks.video.close();
        this.localTracks.video = null;
      }
      if (this.client) {
        this.client.leave();
        this.client = null;
      }
    },
  },
};
</script>

<style scoped>
.video-call-page {
  position: fixed;
  inset: 0;
  background: #1a1a2e;
  display: flex;
  flex-direction: column;
  z-index: 9999;
}

.video-call-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: rgba(0,0,0,0.4);
  color: #fff;
  flex-shrink: 0;
  z-index: 10;
}

.meeting-code-label {
  font-size: 0.9rem;
  opacity: 0.8;
  font-family: monospace;
}

.controls {
  display: flex;
  gap: 12px;
}

.ctrl-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.15);
  color: #fff;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.ctrl-btn:hover    { background: rgba(255,255,255,0.3); }
.ctrl-btn.muted    { background: rgba(255,80,80,0.5); }
.ctrl-btn.recording { background: #e53935; animation: pulse-red 1.2s infinite; }
.ctrl-btn.active   { background: rgba(99,179,237,0.4); }
.ctrl-btn.end-btn  { background: #e53935; }
.ctrl-btn.encounter-btn { background: #7c3aed; }
.ctrl-btn.encounter-btn:hover { background: #5b21b6; }
.tele-reason { display: inline-block; max-width: 320px; margin-left: 10px; opacity: .75; font-size: .85em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; vertical-align: bottom; }
.ctrl-btn.end-btn:hover { background: #b71c1c; }

@keyframes pulse-red {
  0%, 100% { box-shadow: 0 0 0 0 rgba(229,57,53,0.5); }
  50%       { box-shadow: 0 0 0 8px rgba(229,57,53,0); }
}

/* Main layout */
.main-area {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* Video grid */
.video-grid {
  flex: 1;
  position: relative;
  background: #0d0d1a;
}

.remote-video-wrapper {
  position: absolute;
  inset: 0;
}

.local-video-wrapper {
  position: absolute;
  bottom: 24px;
  right: 24px;
  width: 200px;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid rgba(255,255,255,0.3);
  z-index: 10;
}

/* When notes open, shift local video pip */
.notes-open .local-video-wrapper {
  right: calc(360px + 24px);
}

.video-player {
  width: 100%;
  height: 100%;
  background: #111;
}

.video-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255,255,255,0.4);
}

.video-label {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0,0,0,0.5);
  color: #fff;
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 4px;
}

/* Notes panel */
.notes-panel {
  width: 360px;
  flex-shrink: 0;
  background: #12122a;
  border-left: 1px solid rgba(255,255,255,0.1);
  display: flex;
  flex-direction: column;
  color: #e2e8f0;
}

.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: rgba(0,0,0,0.3);
  font-weight: 600;
  font-size: 0.9rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  flex-shrink: 0;
}

.notes-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dictating-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #fc8181;
  font-weight: 500;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fc8181;
  animation: pulse-dot 1s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(0.8); }
}

.notes-icon-btn {
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  cursor: pointer;
  padding: 4px;
  font-size: 0.9rem;
  border-radius: 4px;
  transition: color 0.2s;
}
.notes-icon-btn:hover { color: #fc8181; }

.notes-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px;
  gap: 8px;
}

.interim-text {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.35);
  font-style: italic;
  min-height: 20px;
  line-height: 1.4;
  flex-shrink: 0;
}

.notes-textarea {
  flex: 1;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 0.875rem;
  line-height: 1.6;
  padding: 12px;
  resize: none;
  outline: none;
  font-family: inherit;
  transition: border-color 0.2s;
}
.notes-textarea:focus {
  border-color: rgba(99,179,237,0.5);
}
.notes-textarea::placeholder {
  color: rgba(255,255,255,0.2);
}

.notes-footer {
  display: flex;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid rgba(255,255,255,0.08);
  flex-shrink: 0;
}

.btn-dictate, .btn-save {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-dictate {
  background: rgba(255,255,255,0.1);
  color: #e2e8f0;
}
.btn-dictate:hover { background: rgba(255,255,255,0.18); }
.btn-dictate.active {
  background: rgba(229,57,53,0.25);
  color: #fc8181;
}

.btn-save {
  background: rgba(72,187,120,0.2);
  color: #68d391;
}
.btn-save:hover:not(:disabled) { background: rgba(72,187,120,0.35); }
.btn-save:disabled { opacity: 0.4; cursor: not-allowed; }

.save-success, .save-error {
  margin: 0 12px 10px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 6px;
}
.save-success { background: rgba(72,187,120,0.15); color: #68d391; }
.save-error   { background: rgba(229,57,53,0.15);  color: #fc8181; }

.connecting-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
}
</style>
